// Screen 5: Benchmark Memory — research result database.
// Layout: sidebar | (topbar + filter bar + main table + right detail rail)

function BenchScreen() {
  const { t } = useT();
  return (
    <div className="pp pp-screen" data-screen-label="05 Benchmark memory">
      <Sidebar active="bench"/>

      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.workspace'), t('crumb.bench')]}/>}
          search="results on dataset D-NeRF under 8 views condition…"
        />

        {/* Filter bar */}
        <div className="pp-bench-filterbar">
          <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
            <BenchFilter label={t('bn.dataset')}  value="D-NeRF · HyperNeRF" count="2"/>
            <BenchFilter label={t('bn.task')}     value="Dynamic NVS"/>
            <BenchFilter label={t('bn.metric')}   value="PSNR · LPIPS · FPS" count="3"/>
            <BenchFilter label={t('bn.cond')}     value="8 views, 200 fr."/>
            <BenchFilter label={t('bn.method')}   value={t('bn.all')} muted/>
            <BenchFilter label={t('bn.venue')}    value={t('bn.any')} muted/>
            <BenchFilter label={t('bn.year')}     value="2024 — 2026"/>
            <div className="pp-divider-v" style={{ height: 22 }}/>
            <ToggleChip label={t('bn.code')} on/>
            <ToggleChip label={t('bn.verified')}/>
            <ToggleChip label={t('bn.real')}/>
            <ToggleChip label={t('bn.fair')} on/>
          </div>
          <div className="row gap-2" style={{ marginLeft: 'auto' }}>
            <div className="pp-tabs">
              <button className="pp-tab is-active"><I name="table" size={11}/> {t('bn.view.table')}</button>
              <button className="pp-tab"><I name="chart" size={11}/> {t('bn.view.leader')}</button>
              <button className="pp-tab"><I name="compare" size={11}/> {t('bn.view.compare')}</button>
            </div>
          </div>
        </div>

        <div className="pp-content">
          <div className="pp-bench-center scroll">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', padding: '14px 24px 8px' }}>
              <div className="col gap-1">
                <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                  <span className="serif" style={{ fontSize: 20 }}>{t('bn.results')}</span>
                  <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{t('bn.results.sub')}</span>
                </div>
                <div className="row gap-3" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  <span><span className="mono" style={{ color: 'var(--ok)' }}>62</span> {t('bn.stat.verified')}</span>
                  <span><span className="mono" style={{ color: 'var(--acc-cyan)' }}>21</span> {t('bn.stat.auto')}</span>
                  <span><span className="mono" style={{ color: 'var(--warn)' }}>4</span> {t('bn.stat.corrected')}</span>
                  <span><span className="mono" style={{ color: 'var(--danger)' }}>3</span> {t('bn.stat.fair')}</span>
                </div>
              </div>
              <div className="row gap-2">
                <button className="btn sm ghost"><I name="layers" size={12}/> {t('bn.group')}</button>
                <button className="btn sm ghost"><I name="download" size={12}/> {t('bn.export')}</button>
                <button className="btn sm primary"><I name="compare" size={12}/> {t('bn.compare3')}</button>
              </div>
            </div>

            <BenchTable/>
          </div>

          <RailRight id="bench-detail" defaultWidth={380} min={320} max={520} className="pp-bench-rail scroll">
            <BenchDetailPanel/>
          </RailRight>
        </div>
      </div>
    </div>
  );
}

function BenchFilter({ label, value, count, muted }) {
  return (
    <div className={'pp-bench-filter' + (muted ? ' is-muted' : '')}>
      <span className="pp-bench-filter-label">{label}</span>
      <span className="pp-bench-filter-value">{value}</span>
      {count && <span className="mono pp-bench-filter-count">{count}</span>}
      <I name="chevronDown" size={11}/>
    </div>
  );
}

function ToggleChip({ label, on }) {
  const [v, setV] = React.useState(!!on);
  return (
    <button className={'pp-toggle-chip' + (v ? ' is-on' : '')} onClick={() => setV(b => !b)}>
      <span className="pp-toggle-chip-dot"/>
      {label}
    </button>
  );
}

function BenchTable() {
  const { t } = useT();
  // Editable state — when user changes a cell, the row's status becomes 'corrected'
  const [edits, setEdits] = React.useState({});
  const grouped = [
    { header: 'D-NeRF · Dynamic NVS · 8 views, 200 fr.', rows: BENCHMARKS.filter(b => b.d === 'D-NeRF') },
    { header: 'HyperNeRF · Dynamic NVS · casual capture', rows: BENCHMARKS.filter(b => b.d === 'HyperNeRF') },
    { header: 'ALOHA · Bimanual · 50-trial real robot', rows: BENCHMARKS.filter(b => b.d === 'ALOHA') },
  ];
  return (
    <div className="pp-bench-table">
      <div className="pp-bench-th">
        <div className="pp-bench-c-check"><div className="pp-check-box" style={{ width: 14, height: 14 }}/></div>
        <div className="pp-bench-c-method">{t('bn.col.method')}</div>
        <div className="pp-bench-c-paper">{t('bn.col.paper')}</div>
        <div className="pp-bench-c-metric">{t('bn.col.metric')}</div>
        <div className="pp-bench-c-value">{t('bn.col.value')}</div>
        <div className="pp-bench-c-bar">{t('bn.col.delta')}</div>
        <div className="pp-bench-c-code">{t('bn.col.code')}</div>
        <div className="pp-bench-c-src">{t('bn.col.source')}</div>
        <div className="pp-bench-c-conf">{t('bn.col.conf')}</div>
        <div className="pp-bench-c-status">{t('bn.col.status')}</div>
        <div className="pp-bench-c-actions"></div>
      </div>

      {grouped.map((g, gi) => {
        // find best value per metric within group
        return (
          <React.Fragment key={gi}>
            <div className="pp-bench-group-header">
              <I name="chevronDown" size={11} style={{ color: 'var(--fg-3)' }}/>
              <span className="mono" style={{ fontSize: 11, color: 'var(--acc-indigo)', letterSpacing: '0.08em' }}>{g.header}</span>
              <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginLeft: 'auto' }}>{g.rows.length} {t('bn.row.results')}</span>
            </div>
            {g.rows.map((r, i) => {
              const paper = PAPERS.find(p => p.id === r.p);
              const editKey = `${r.d}-${r.t}-${r.m}-${r.metric}`;
              const editedValue = edits[editKey];
              const displayValue = editedValue !== undefined ? editedValue : r.v;
              const isEdited = editedValue !== undefined;
              const effectiveStatus = isEdited ? 'corrected' : r.s;
              const best = Math.max(...g.rows.filter(x => x.metric === r.metric).map(x => x.metric.includes('↓') ? -x.v : x.v));
              const ratio = (r.metric.includes('↓') ? -displayValue : displayValue) / best;
              const isBest = Math.abs((r.metric.includes('↓') ? -displayValue : displayValue) - best) < 1e-6;
              return (
                <div key={i} className={'pp-bench-tr' + (isBest ? ' is-best' : '') + (r.m === 'GS-DiT' && r.metric.startsWith('PSNR') ? ' is-selected' : '')}>
                  <div className="pp-bench-c-check">
                    <div className={'pp-check-box' + (r.m === 'GS-DiT' && r.metric.startsWith('PSNR') ? ' is-on' : '')} style={{ width: 14, height: 14, background: r.m === 'GS-DiT' && r.metric.startsWith('PSNR') ? 'var(--acc-indigo)' : 'transparent', borderColor: r.m === 'GS-DiT' && r.metric.startsWith('PSNR') ? 'var(--acc-indigo)' : 'var(--border-2)' }}>
                      {r.m === 'GS-DiT' && r.metric.startsWith('PSNR') && <I name="check" size={10}/>}
                    </div>
                  </div>
                  <div className="pp-bench-c-method">
                    {isBest && <span className="pp-bench-crown">★</span>}
                    <span className={isBest ? 'pp-bench-best-name' : ''}>{r.m}</span>
                  </div>
                  <div className="pp-bench-c-paper ellipsis" title={paper?.title}>
                    {paper ? <span className="mono">{paper.venue.replace(/ \d+/, '')} ’{paper.venue.match(/\d{2}/)?.[0] || '26'}</span> : <span style={{ color: 'var(--fg-3)' }}>–</span>}
                  </div>
                  <div className="pp-bench-c-metric mono">{r.metric}</div>
                  <EditableValueCell
                    value={displayValue}
                    edited={isEdited}
                    onChange={(v) => {
                      const cleaned = parseFloat(v);
                      if (!isNaN(cleaned)) setEdits(e => ({ ...e, [editKey]: cleaned }));
                    }}
                    onReset={() => setEdits(e => { const n = { ...e }; delete n[editKey]; return n; })}
                  />
                  <div className="pp-bench-c-bar">
                    <div className="pp-bench-deltabar">
                      <div className="pp-bench-deltabar-fill" style={{
                        width: `${Math.min(100, ratio * 100)}%`,
                        background: isBest ? 'linear-gradient(90deg, var(--acc-indigo), var(--acc-violet))' : 'linear-gradient(90deg, rgba(136,150,255,0.4), rgba(136,150,255,0.15))',
                      }}/>
                    </div>
                  </div>
                  <div className="pp-bench-c-code">
                    {r.code ? <span className="chip ok" style={{ height: 18 }}><I name="github" size={10}/></span> : <span className="chip ghost" style={{ height: 18, color: 'var(--fg-4)' }}>—</span>}
                  </div>
                  <div className="pp-bench-c-src ellipsis mono" style={{ color: 'var(--fg-2)' }}>{r.src}</div>
                  <div className="pp-bench-c-conf">
                    <div className="pp-conf-bar">
                      <div className="pp-conf-bar-fill" style={{ width: `${r.conf * 100}%`, background: r.conf > 0.9 ? 'var(--ok)' : r.conf > 0.8 ? 'var(--acc-cyan)' : 'var(--warn)' }}/>
                    </div>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-2)' }}>{Math.round(r.conf * 100)}</span>
                  </div>
                  <div className="pp-bench-c-status">
                    <StatusBadge status={effectiveStatus} warn={r.warn}/>
                  </div>
                  <div className="pp-bench-c-actions">
                    <button className="iconbtn"><I name="external" size={12}/></button>
                    <button className="iconbtn"><I name="more" size={12}/></button>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StatusBadge({ status, warn }) {
  const { t } = useT();
  if (status === 'verified') return <span className="chip ok" style={{ height: 20 }}><I name="shield" size={10}/> {t('bn.status.verified')}</span>;
  if (status === 'corrected') return <span className="chip cyan" style={{ height: 20 }}><I name="check" size={10}/> {t('bn.status.corrected')}</span>;
  if (warn) return <span className="chip warn" style={{ height: 20 }}><I name="warning" size={10}/> {t('bn.status.fair')}</span>;
  return <span className="chip ghost" style={{ height: 20 }}><I name="sparkles" size={10}/> {t('bn.status.auto')}</span>;
}

// Editable benchmark value cell — click to edit, ↵ to save, Esc to cancel.
function EditableValueCell({ value, edited, onChange, onReset }) {
  const { lang } = useT();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (editing) {
      setDraft(String(value));
      setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 0);
    }
  }, [editing, value]);

  const commit = () => {
    if (draft !== String(value)) onChange(draft);
    setEditing(false);
  };
  const cancel = () => setEditing(false);

  const display = typeof value === 'number' && value < 1 ? value.toFixed(3) : value;

  if (editing) {
    return (
      <div className="pp-bench-c-value pp-bench-edit">
        <input
          ref={inputRef}
          className="pp-bench-edit-input mono"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') cancel();
          }}
          onBlur={commit}
        />
      </div>
    );
  }

  return (
    <div
      className={'pp-bench-c-value mono pp-bench-editable' + (edited ? ' is-edited' : '')}
      onClick={() => setEditing(true)}
      title={lang === 'ko' ? '클릭하여 값 보정' : 'Click to correct'}
    >
      {display}
      {edited && (
        <button
          className="pp-bench-edit-reset"
          onClick={(e) => { e.stopPropagation(); onReset(); }}
          title={lang === 'ko' ? '보정 취소' : 'Reset correction'}
        >
          <I name="close" size={9}/>
        </button>
      )}
    </div>
  );
}

function BenchDetailPanel() {
  const { t } = useT();
  return (
    <div className="col gap-3">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="section-title"><span className="num">{t('bn.rail.detail')}</span></div>
        <RailCollapseBtn/>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div className="row gap-2" style={{ alignItems: 'baseline' }}>
          <span className="chip accent">CVPR 2026</span>
          <span className="chip ok" style={{ height: 20 }}><I name="shield" size={10}/> verified</span>
        </div>
        <div className="serif" style={{ fontSize: 17, marginTop: 6, lineHeight: 1.2 }}>GS-DiT on D-NeRF</div>
        <div style={{ fontSize: 11.5, color: 'var(--fg-2)', marginTop: 2 }}>Tancik, Yu, Park, Mildenhall, Srinivasan</div>

        <div className="pp-bench-bignum">
          <div className="col">
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>PSNR</span>
            <span className="serif" style={{ fontSize: 38, color: 'var(--acc-indigo)', lineHeight: 1 }}>31.74</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--ok)' }}>+1.32 vs best (Def-3DGS · 30.42)</span>
          </div>
          <div className="col" style={{ flex: 1, gap: 6, paddingTop: 4 }}>
            <BenchSecondary label="LPIPS ↓" value="0.041" delta="−0.012" pos/>
            <BenchSecondary label="FPS ↑"   value="84"    delta="3.1× speedup" pos/>
            <BenchSecondary label="GPU h"   value="11"    delta="single 4090"/>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div className="section-title" style={{ marginBottom: 8 }}>{t('bn.rail.cond')}</div>
        <div className="col gap-2">
          <KV k={t('bn.rail.cond.views')}    v={t('bn.rail.cond.views.v')}/>
          <KV k={t('bn.rail.cond.frames')}    v={t('bn.rail.cond.frames.v')}/>
          <KV k={t('bn.rail.cond.res')}      v="800 × 800"/>
          <KV k={t('bn.rail.cond.split')}    v={t('bn.rail.cond.split.v')}/>
          <KV k={t('bn.rail.cond.hw')}        v={t('bn.rail.cond.hw.v')}/>
          <KV k={t('bn.rail.cond.pre')}      v={t('bn.rail.cond.pre.v')}/>
        </div>
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div className="section-title">{t('bn.rail.evidence')}</div>
          <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>Tbl. 2, p. 6</span>
        </div>
        <div className="pp-source-snippet">
          <div className="row mono" style={{ fontSize: 9.5, color: '#888', letterSpacing: '0.06em' }}>
            <span style={{ flex: 1 }}>Method</span>
            <span style={{ width: 50, textAlign: 'right' }}>PSNR</span>
            <span style={{ width: 50, textAlign: 'right' }}>LPIPS</span>
            <span style={{ width: 36, textAlign: 'right' }}>FPS</span>
          </div>
          {[
            { m: 'K-Planes',       a: '29.61', b: '.071', c: '0.3' },
            { m: 'Def-3DGS',       a: '30.42', b: '.053', c: '27' },
            { m: '4DGS-Cache',     a: '31.21', b: '.044', c: '120' },
            { m: 'GS-DiT (ours)',  a: '31.74', b: '.041', c: '84', hl: true },
          ].map((r, i) => (
            <div key={i} className="row" style={{ fontSize: 10, padding: '2px 0', color: r.hl ? '#5B68E0' : '#222', fontWeight: r.hl ? 700 : 400 }}>
              <span style={{ flex: 1 }}>{r.m}</span>
              <span className="mono" style={{ width: 50, textAlign: 'right' }}>{r.a}</span>
              <span className="mono" style={{ width: 50, textAlign: 'right' }}>{r.b}</span>
              <span className="mono" style={{ width: 36, textAlign: 'right' }}>{r.c}</span>
            </div>
          ))}
        </div>
        <button className="btn sm ghost" style={{ marginTop: 8 }}><I name="external" size={11}/> {t('bn.rail.openpdf')}</button>
      </div>

      <div className="card pp-fair-warn" style={{ padding: 12 }}>
        <div className="row gap-2" style={{ alignItems: 'baseline' }}>
          <I name="warning" size={13} style={{ color: 'var(--warn)' }}/>
          <span style={{ fontSize: 11, color: 'var(--warn)', textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mono">{t('bn.rail.fair.kicker')}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg-1)', marginTop: 6, lineHeight: 1.5 }}>
          {t('bn.rail.fair.body')}
        </div>
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div className="section-title" style={{ marginBottom: 6 }}>{t('bn.rail.ai')}</div>
        <div style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.55 }}>
          {t('bn.rail.ai.body')}
        </div>
      </div>
    </div>
  );
}

function BenchSecondary({ label, value, delta, pos }) {
  return (
    <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border-0)' }}>
      <span style={{ fontSize: 11, color: 'var(--fg-2)' }} className="mono">{label}</span>
      <span className="row gap-2" style={{ alignItems: 'baseline' }}>
        <span className="mono" style={{ fontSize: 13, color: 'var(--fg-0)' }}>{value}</span>
        <span className="mono" style={{ fontSize: 10, color: pos ? 'var(--ok)' : 'var(--fg-3)' }}>{delta}</span>
      </span>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', fontSize: 11.5 }}>
      <span style={{ color: 'var(--fg-3)' }}>{k}</span>
      <span style={{ color: 'var(--fg-0)' }} className="mono">{v}</span>
    </div>
  );
}

if (!document.getElementById('pp-bench-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-bench-styles';
  s.textContent = `
  .pp-bench-filterbar {
    padding: 10px 16px;
    border-bottom: 1px solid var(--border-1);
    background: var(--bg-1);
    display: flex; align-items: center; gap: 10px;
    flex-wrap: wrap;
  }
  .pp-bench-filter {
    display: flex; align-items: center; gap: 6px;
    height: 26px; padding: 0 10px;
    border-radius: 6px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    cursor: pointer;
  }
  .pp-bench-filter.is-muted { opacity: 0.6; }
  .pp-bench-filter-label {
    font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--fg-3);
  }
  .pp-bench-filter-value { font-size: 11.5px; color: var(--fg-0); }
  .pp-bench-filter-count {
    font-family: var(--font-mono); font-size: 10px; color: var(--acc-indigo);
    padding: 1px 5px; border-radius: 999px;
    background: rgba(136,150,255,0.12);
  }
  .pp-toggle-chip {
    display: inline-flex; align-items: center; gap: 6px;
    height: 26px; padding: 0 10px;
    border-radius: 6px;
    background: var(--bg-2); border: 1px solid var(--border-1);
    color: var(--fg-2); font-size: 11.5px;
    cursor: pointer; transition: all .12s;
  }
  .pp-toggle-chip-dot {
    width: 6px; height: 6px; border-radius: 999px; background: var(--fg-4);
  }
  .pp-toggle-chip.is-on { background: rgba(136,150,255,0.10); border-color: rgba(136,150,255,0.40); color: var(--fg-0); }
  .pp-toggle-chip.is-on .pp-toggle-chip-dot { background: var(--acc-indigo); box-shadow: 0 0 6px var(--acc-indigo); }

  .pp-bench-center {
    flex: 1; min-width: 0;
    overflow: auto;
    display: flex; flex-direction: column;
  }
  .pp-bench-rail {
    width: 380px; flex-shrink: 0;
    border-left: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 18px;
    overflow: auto;
  }

  .pp-bench-table {
    margin: 0 16px 16px;
    border: 1px solid var(--border-1);
    border-radius: 10px;
    overflow: hidden;
    background: var(--bg-1);
  }
  .pp-bench-th, .pp-bench-tr {
    display: grid;
    grid-template-columns: 28px 1.5fr 0.9fr 0.85fr 0.75fr 1fr 0.5fr 1fr 0.7fr 0.95fr 56px;
    align-items: center;
    padding: 0 12px;
    gap: 10px;
  }
  .pp-bench-th {
    height: 34px;
    background: var(--bg-2);
    border-bottom: 1px solid var(--border-1);
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-3);
  }
  .pp-bench-group-header {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 12px;
    background: rgba(136,150,255,0.04);
    border-top: 1px solid var(--border-1);
    border-bottom: 1px solid var(--border-1);
  }
  .pp-bench-tr {
    height: 38px;
    border-top: 1px solid var(--border-0);
    font-size: 12px;
    color: var(--fg-1);
    cursor: pointer;
  }
  .pp-bench-tr:hover { background: rgba(255,255,255,0.025); }
  .pp-bench-tr.is-selected {
    background: rgba(136,150,255,0.06);
    box-shadow: inset 2px 0 0 var(--acc-indigo);
  }
  .pp-bench-tr.is-best .pp-bench-best-name { color: var(--acc-indigo); font-weight: 500; }
  .pp-bench-crown {
    color: var(--acc-violet);
    margin-right: 4px;
    font-size: 11px;
  }
  .pp-bench-c-method { display: flex; align-items: center; }
  .pp-bench-c-actions { display: flex; gap: 0; justify-content: flex-end; }

  .pp-bench-deltabar {
    height: 4px; background: rgba(255,255,255,0.04); border-radius: 999px; overflow: hidden;
  }
  .pp-bench-deltabar-fill { height: 100%; border-radius: 999px; }
  .pp-conf-bar {
    display: inline-block; width: 30px; height: 3px;
    background: rgba(255,255,255,0.06); border-radius: 999px;
    margin-right: 6px; overflow: hidden;
  }
  .pp-conf-bar-fill { height: 100%; border-radius: 999px; }
  .pp-bench-c-conf { display: flex; align-items: center; }

  /* Inline-editable value cell */
  .pp-bench-editable {
    cursor: text;
    padding: 2px 6px;
    margin: 0 -6px;
    border-radius: 4px;
    transition: background .12s, box-shadow .12s;
    position: relative;
  }
  .pp-bench-editable:hover {
    background: rgba(136,150,255,0.10);
    box-shadow: inset 0 0 0 1px rgba(136,150,255,0.30);
  }
  .pp-bench-editable.is-edited {
    color: var(--acc-cyan);
    background: rgba(106,215,240,0.08);
    box-shadow: inset 0 0 0 1px rgba(106,215,240,0.30);
  }
  .pp-bench-edit-reset {
    position: absolute; right: -6px; top: -6px;
    width: 14px; height: 14px; border-radius: 999px;
    background: var(--bg-4);
    border: 1px solid var(--border-2);
    color: var(--fg-2);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    opacity: 0; transition: opacity .12s;
  }
  .pp-bench-editable:hover .pp-bench-edit-reset { opacity: 1; }
  .pp-bench-edit { padding: 0; }
  .pp-bench-edit-input {
    width: 100%; height: 22px; padding: 0 6px;
    background: var(--bg-1);
    border: 1px solid rgba(136,150,255,0.50);
    border-radius: 4px;
    color: var(--fg-0);
    font-size: 12px;
    box-shadow: 0 0 0 3px rgba(136,150,255,0.12);
  }
  .pp-bench-edit-input:focus { outline: none; }

  .pp-bench-bignum {
    margin-top: 12px; padding-top: 12px;
    border-top: 1px dashed var(--border-1);
    display: flex; gap: 16px;
  }

  .pp-source-snippet {
    background: #efece5;
    color: #222;
    padding: 12px 14px;
    border-radius: 6px;
    font-family: 'Newsreader', serif;
    box-shadow: inset 0 0 0 1px rgba(0,0,0,0.05);
  }
  .pp-fair-warn {
    background: linear-gradient(180deg, rgba(232,184,106,0.06), transparent);
    border-color: rgba(232,184,106,0.30);
  }
  `;
  document.head.appendChild(s);
}

export { BenchScreen };
